import type { StudentRow } from '../../../shared/api/classes'
import type { EventRow, SkillStanding, StrengthsReport } from '../../../shared/api/teaching'
import type { ProgressSnapshot, SessionRow } from '../../../shared/api/student'
import { GAME_TITLES } from '../../../shared/ui/gameTitles'
import type { GameId } from '../../../shared/curriculum'
import type { Lang } from './copy'
import {
  ADMIN_COPY,
  BAND_LABEL,
  EVENT_LABEL,
  SKILL_LABEL,
  STANDING_LABEL,
} from './adminCopy'

/**
 * What goes on the printed profile, as data.
 *
 * 🔴 THE DOCUMENT IS ASSEMBLED HERE RATHER THAN IN THE SHEET'S JSX, AND THE
 * REASON IS THE ONE RULE THIS PAGE CANNOT BREAK. `SchoolStudentReport`'s
 * Overview tab still draws four invented subject percentages ("Math 92%"), an
 * attendance ring nobody computed and a hardcoded "Joined On 10 Jun 2025" — the
 * `|| 348` defect `SchoolDashboard` had its whole tile strip rewritten for. A
 * screen carrying an invented number is bad; a PRINTED PAGE about a named child,
 * on a head teacher's desk, in a meeting with that child's parents, is the last
 * place one should be able to reach. So the sheet takes what this file returns
 * and nothing else, and every function here is a pure mapping over data that
 * came off a real request.
 *
 * 🔴 AND "NOT LOADED" IS A THIRD STATE, NOT ZERO. Each panel on the screen owns
 * its own request (§8.4), so a print taken while one is failing or in flight
 * must say so — a section that printed "0 sittings" for a read that never
 * arrived would be a false statement about a child who plays every day.
 * `window.print()` is synchronous, so there is no moment in which to wait for
 * one: the honest answer is the only answer available.
 */

/** One labelled fact. Both halves are already localised strings. */
export interface SheetRow {
  label: string
  value: string
  /** A second line under the value — evidence, never decoration. */
  detail?: string
}

/**
 * ⚠️ THREE STATES, AND THE MIDDLE ONE IS THE POINT. `rows` is data, `empty` is a
 * real answer about a child who has not played, `not-loaded` is the read that
 * did not arrive before the dialogue opened.
 */
export type SheetSectionState = 'rows' | 'empty' | 'not-loaded'

export interface SheetSection {
  title: string
  state: SheetSectionState
  rows: SheetRow[]
  /** Printed under the heading: what the reader is looking at. */
  note?: string
}

/**
 * How many rows of a capped list reach the page.
 *
 * ⚠️ THE SHEET IS A PROFILE, NOT AN EXPORT OF THE EVENT LOG. Fifty events is
 * three pages of near-identical lines that bury the six sections above them, and
 * the screen's own table already offers the full list to somebody who wants it.
 * The count printed with the section says what was left out, which is the part a
 * silent truncation gets wrong.
 */
export const SHEET_SESSIONS = 10
export const SHEET_EVENTS = 15

const dash = (lang: Lang): string => ADMIN_COPY[lang].dash

/** A date a person can read, or the em dash when there is nothing to print. */
function when(iso: string | null | undefined, lang: Lang): string {
  if (!iso) return dash(lang)
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? dash(lang) : at.toLocaleDateString()
}

function whenExact(iso: string, lang: Lang): string {
  const at = new Date(iso)
  return Number.isNaN(at.getTime()) ? dash(lang) : at.toLocaleString()
}

/**
 * The child, as the roster holds them.
 *
 * ⚠️ ADR-004's CEILING IS THE WHOLE LIST. A name, a roll, a class, a band and a
 * guardian — there is no date of birth, address, photograph or note to print,
 * and `check-no-free-text.mjs` fails the build if anyone adds one. The sheet
 * states that in `sheetSource` rather than leaving a reader to wonder what was
 * withheld.
 */
export function identityRows(student: StudentRow, lang: Lang): SheetRow[] {
  const t = ADMIN_COPY[lang]
  return [
    { label: t.childName, value: student.name },
    { label: t.roll, value: student.rollNumber ?? dash(lang) },
    { label: t.sheetClass, value: student.className },
    {
      label: t.band,
      value: BAND_LABEL[student.ageBand]?.[lang] ?? student.ageBand,
    },
    {
      label: t.parent,
      /* 🔴 THE ABSENCE IS A FINDING, NOT A BLANK. E7 counts these children on a
         tile of their own: no name and no number means no report can reach a
         family, which is exactly what a printed profile should surface. */
      value: student.parentName ?? t.noParent,
      detail: student.parentName === null ? t.noParentWhy : undefined,
    },
    {
      label: t.parentPhoneLabel,
      /* ⚠️ MASKED, EXACTLY AS THE SCREEN SHOWS IT. The full number never leaves
         the server, and a document is the easiest thing in the product to
         forward — see `phoneMasked`. */
      value: student.parentPhone ?? dash(lang),
    },
  ]
}

/**
 * Coins, stickers, the streak and the last day they played.
 *
 * ⚠️ ZEROES ARE A REAL ANSWER HERE AND ARE PRINTED AS ONE. `GET /progress/:id`
 * answers zeroes rather than a 404 for a child who has never played, so an empty
 * snapshot is a fact about a real child — "not started", never "failed to load".
 */
export function progressSection(
  progress: ProgressSnapshot | null,
  lang: Lang,
): SheetSection {
  const t = ADMIN_COPY[lang]
  if (progress === null) {
    return { title: t.sheetProgress, state: 'not-loaded', rows: [] }
  }

  return {
    title: t.sheetProgress,
    state: 'rows',
    rows: [
      { label: t.coins, value: String(progress.coins) },
      { label: t.stickers, value: String(progress.stickers.length) },
      { label: t.streak, value: String(progress.streakDays) },
      {
        label: t.lastPlayed,
        value: progress.lastPlayed === null ? t.neverPlayedShort : when(progress.lastPlayed, lang),
      },
      { label: t.sheetEventsCounted, value: String(progress.skills.totalEvents) },
    ],
  }
}

/**
 * What they are learning, as levels.
 *
 * 🔴 A LEVEL, NEVER A PERCENTAGE OF A CURRICULUM (§8, ADR-037) — which is
 * precisely what the screen's Overview tab prints four of. `levelCompleted` is
 * the number the server measured; "Not started" is the honest word for zero.
 */
export function skillsSection(
  progress: ProgressSnapshot | null,
  lang: Lang,
): SheetSection {
  const t = ADMIN_COPY[lang]
  if (progress === null) {
    return { title: t.sheetSkills, state: 'not-loaded', rows: [] }
  }

  const rows = progress.skills.content.map((c) => ({
    label: SKILL_LABEL[c.skill]?.[lang] ?? c.skill,
    value: c.levelCompleted > 0 ? t.level(c.levelCompleted) : t.notStartedSkill,
  }))

  return {
    title: t.sheetSkills,
    state: rows.length === 0 ? 'empty' : 'rows',
    rows,
    note: t.contentSkills,
  }
}

/**
 * One standing, as a sentence a reader can check.
 *
 * 🔴 THE NUMERATOR AND THE DENOMINATOR, ALWAYS — OD-39 and `SkillStanding`'s own
 * note. "Strong" on its own is a claim about somebody's four-year-old; "Strong ·
 * 9 right of 12 answered" is a claim with its evidence attached, and the
 * comparison prints the class's own figure beside it or nothing at all.
 */
export function standingRow(row: SkillStanding, lang: Lang): SheetRow {
  const t = ADMIN_COPY[lang]
  const parts = [t.evidence(row.correct, row.attempts)]
  if (row.classAccuracy !== null) {
    parts.push(t.classShare(Math.round(row.classAccuracy * 100)))
  }
  if (row.topLevel !== null) parts.push(t.topLevel(row.topLevel))
  /* 🔴 `play.prisma` — the only signal that a level's content was too hard. It
     is on the sheet because it is the one number a parents' meeting is about. */
  if (row.ceiling > 0) parts.push(t.ceiling(row.ceiling))

  return {
    label: SKILL_LABEL[row.skill]?.[lang] ?? row.skill,
    value: STANDING_LABEL[row.standing]?.[lang] ?? row.standing,
    detail: parts.join(' · '),
  }
}

export function strengthsSection(
  strengths: StrengthsReport | null,
  lang: Lang,
): SheetSection {
  const t = ADMIN_COPY[lang]
  if (strengths === null) {
    return { title: t.sheetStrengths, state: 'not-loaded', rows: [] }
  }

  return {
    title: t.sheetStrengths,
    state: strengths.skills.length === 0 ? 'empty' : 'rows',
    rows: strengths.skills.map((row) => standingRow(row, lang)),
    /* ⚠️ THE PEER COUNT TRAVELS WITH THE COMPARISON. "Compared with 0
       classmates" is why a standing may read "Too early to say", and a document
       without it invites the reader to supply their own denominator. */
    note: `${t.strengthsHint} ${t.strengthsPeers(strengths.peers)}`,
  }
}

/**
 * When this child sat down to play.
 *
 * ⚠️ "NOT RECORDED" IS NOT "PLAYED BADLY" (ADR-030). A sitting with
 * `recorded: false` wrote no activity at all — the recording chain was off — and
 * printing it as zero events would put a privacy setting on the page as if it
 * were a fact about the child.
 */
export function sessionsSection(
  sessions: SessionRow[] | null,
  lang: Lang,
  limit = SHEET_SESSIONS,
): SheetSection {
  const t = ADMIN_COPY[lang]
  if (sessions === null) {
    return { title: t.sheetSessions, state: 'not-loaded', rows: [] }
  }

  const shown = sessions.slice(0, limit)
  return {
    title: t.sheetSessions,
    state: shown.length === 0 ? 'empty' : 'rows',
    rows: shown.map((s) => ({
      label: whenExact(s.startedAt, lang),
      value: s.recorded ? t.activities(s.events) : t.sessionNotRecorded,
      detail: s.recorded && s.endedAt === null ? t.sessionOpen : undefined,
    })),
    note:
      sessions.length > shown.length
        ? t.sheetShowing(shown.length, sessions.length)
        : t.sessionsHint,
  }
}

/** What actually happened, newest first — the same rows the Activity tab shows. */
export function activitySection(
  events: EventRow[] | null,
  lang: Lang,
  limit = SHEET_EVENTS,
): SheetSection {
  const t = ADMIN_COPY[lang]
  if (events === null) {
    return { title: t.sheetActivity, state: 'not-loaded', rows: [] }
  }

  const shown = events.slice(0, limit)
  return {
    title: t.sheetActivity,
    state: shown.length === 0 ? 'empty' : 'rows',
    rows: shown.map((e) => {
      const bits: string[] = [GAME_TITLES[e.game as GameId]?.[lang] ?? e.game]
      if (e.skill !== null) bits.push(SKILL_LABEL[e.skill]?.[lang] ?? e.skill)
      if (e.level !== null) bits.push(`${t.activityLevel} ${String(e.level)}`)
      return {
        label: whenExact(e.createdAt, lang),
        value: EVENT_LABEL[e.type]?.[lang] ?? e.type,
        detail: bits.join(' · '),
      }
    }),
    note:
      events.length > shown.length
        ? t.sheetShowing(shown.length, events.length)
        : undefined,
  }
}

export interface SheetInput {
  student: StudentRow
  progress: ProgressSnapshot | null
  strengths: StrengthsReport | null
  sessions: SessionRow[] | null
  events: EventRow[] | null
  lang: Lang
}

/**
 * The whole document, in order.
 *
 * ⚠️ THE ORDER IS THE READING ORDER AND IT IS NOT ALPHABETICAL: who the child
 * is, what they have earned, what they are learning, where they are strong and
 * stuck, then the raw sittings and events a reader only reaches if they want to
 * check the four sections above.
 */
export function sheetSections(input: SheetInput): SheetSection[] {
  const { lang } = input
  return [
    progressSection(input.progress, lang),
    skillsSection(input.progress, lang),
    strengthsSection(input.strengths, lang),
    sessionsSection(input.sessions, lang),
    activitySection(input.events, lang),
  ]
}
